// Visit drafts: one per site until it is closed, saved on every change.
//
// INC-05a: a draft is the whole document. Every change to a started draft
// bumps its revision and rewrites the queued item (D-87); a frozen draft
// is history and the site opens a new one.
import type { GeoPoint, ObservationScope } from '../api/types';
import type {
  ConversionReason,
  DraftObservation,
  DraftStatus,
  FieldcoreDb,
  MissingItem,
  OutboxRecord,
  SiteFormRecord,
  UnplannedReasonCode,
  VisitDraftRecord,
} from '../db/database';
import type { FieldValue } from '../form/values';
import { reasonTextOf, unplannedReason } from '../planning/reasons';
import { enqueueRevision } from '../sync/documents';

export interface OpenedDraft {
  draft: VisitDraftRecord;
  form: SiteFormRecord;
}

/** What a draft is born as (INC-06b-2b): whose it is, and, when it comes from
 *  a slot, which slot it fulfils and the crew that slot suggests (D-152). */
export interface DraftOrigin {
  authorId: string | null;
  visit_kind?: 'planned' | 'unplanned';
  visit_slot_id?: string | null;
  companions?: string[];
}

export function newDraft(
  siteId: string,
  form: SiteFormRecord,
  now: number,
  origin: DraftOrigin,
): VisitDraftRecord {
  return {
    client_generated_id: crypto.randomUUID(),
    site_id: siteId,
    status: 'preparing',
    started_at: null,
    unplanned_reason: null,
    template_version_id: form.template_version_id,
    form_content_hash: form.content_hash,
    document: form.document,
    values: {},
    value_times: {},
    observations: [],
    photos: [],
    geo_start: null,
    geo_end: null,
    ended_at: null,
    client_revision: 0,
    synced_revision: 0,
    missing: null,
    frozen_reason: null,
    server_visit_id: null,
    server_status: null,
    version: null,
    sync_status: 'pending_local',
    visit_kind: origin.visit_kind ?? 'unplanned',
    visit_slot_id: origin.visit_slot_id ?? null,
    unplanned_reason_code: null,
    companions: origin.companions ?? [],
    rejected_companions: [],
    converted_reason: null,
    rejected_code: null,
    author_id: origin.authorId,
    created_at: now,
    updated_at: now,
  };
}

/** The site's current draft, created from its local form when there is none
 *  or when the last one is frozen. A draft not yet started follows the
 *  latest local form; a started one keeps the snapshot it was started
 *  against. Null when the site has no form.
 *
 *  INC-06b-2b (D-113, D-187): the origin names the author and, from a slot,
 *  the slot and its crew. Only a draft that has not started takes them: once
 *  the envelope is born its kind and its slot are fixed, and its author is
 *  the operator that created it whoever opens the screen afterwards.
 *
 *  Only the draft of the operator asking for it is returned: another
 *  operator's open work on the same device is never continued from here. */
export async function openDraftForSite(
  database: FieldcoreDb,
  siteId: string,
  origin: DraftOrigin,
): Promise<OpenedDraft | null> {
  const form = await database.site_forms.get(siteId);
  if (form === undefined) return null;
  return database.transaction('rw', database.visit_drafts, async () => {
    const drafts = await database.visit_drafts.where('site_id').equals(siteId).toArray();
    drafts.sort((a, b) => b.updated_at - a.updated_at);
    const mine = drafts.filter((draft) => draft.author_id === origin.authorId);
    // INC-06b-2b (D-177): a slot is opened again on the visit that fulfilled
    // it, closed or not. The operator sees the work of that slot as it is and
    // never takes a closed visit up again; a new visit of the day is the
    // unplanned way in, or another slot.
    const ofSlot =
      origin.visit_slot_id == null
        ? undefined
        : mine.find((draft) => draft.visit_slot_id === origin.visit_slot_id);
    const current = ofSlot ?? mine.find((draft) => draft.status !== 'frozen');
    const now = Date.now();
    if (current === undefined) {
      const created = newDraft(siteId, form, now, origin);
      await database.visit_drafts.add(created);
      return { draft: created, form };
    }
    if (current.status === 'preparing') {
      const refreshed: VisitDraftRecord = {
        ...current,
        // Criterion 77: a draft that never started holds nothing. A device
        // upgraded from before the rule may still carry what was typed on a
        // visit that was never begun, and that would ride into the first
        // revision of the next visit of this site; it is emptied here, which
        // is the only moment it can be reached. Nothing legitimate is lost:
        // since criterion 76 no write reaches a draft that has not started.
        values: {},
        value_times: {},
        observations: [],
        // A draft that has not started follows the latest local form and the
        // origin it is being opened from.
        ...(current.form_content_hash === form.content_hash
          ? {}
          : {
              template_version_id: form.template_version_id,
              form_content_hash: form.content_hash,
              document: form.document,
            }),
        visit_kind: origin.visit_kind ?? current.visit_kind,
        visit_slot_id: origin.visit_slot_id ?? current.visit_slot_id,
        companions: origin.companions ?? current.companions,
        updated_at: now,
      };
      await database.visit_drafts.put(refreshed);
      return { draft: refreshed, form };
    }
    return { draft: current, form };
  });
}

/** What can still be written into (criterion 76).
 *
 *  Only the visit being captured. Not before it starts: a value typed then
 *  would belong to no visit and carry no start instant. Not while the close
 *  is in flight, and not once the server closed it or refused every later
 *  revision (D-89, D-90). The screens read this rule and so does every write
 *  below it, so no path can get around it. */
export function isEditable(status: DraftStatus): boolean {
  return status === 'started';
}

/** Apply a change to the draft and queue the next revision. A draft that is
 *  not being captured accepts no change at all. Returns the pending outbox
 *  item when one was written or marked, so the caller can wake the engine.
 *  The change receives the instant the draft is saved at. */
export async function mutateDraft(
  database: FieldcoreDb,
  clientGeneratedId: string,
  change: (draft: VisitDraftRecord, now: number) => VisitDraftRecord,
): Promise<OutboxRecord | null> {
  return database.transaction('rw', [database.visit_drafts, database.outbox_data], async () => {
    const draft = await database.visit_drafts.get(clientGeneratedId);
    if (draft === undefined || !isEditable(draft.status)) return null;
    const now = Date.now();
    const changed: VisitDraftRecord = { ...change(draft, now), updated_at: now };
    return enqueueRevision(database, changed, now);
  });
}

/** The reason of an unplanned visit, kept on the draft as soon as the operator
 *  chose it (D-182). A draft that has not started queues nothing, so this only
 *  writes locally; what it buys is that a reload — or Android discarding the
 *  tab while the camera is up — never loses the choice. */
export async function saveUnplannedReason(
  database: FieldcoreDb,
  clientGeneratedId: string,
  code: UnplannedReasonCode,
  text: string,
): Promise<void> {
  await database.transaction('rw', database.visit_drafts, async () => {
    const draft = await database.visit_drafts.get(clientGeneratedId);
    if (draft?.status !== 'preparing') return;
    const reason = unplannedReason(code, text);
    if (draft.unplanned_reason_code === code && draft.unplanned_reason === reason) return;
    await database.visit_drafts.put({
      ...draft,
      unplanned_reason_code: code,
      unplanned_reason: reason,
      updated_at: Date.now(),
    });
  });
}

/** When each value of the draft was written, with ``fieldId`` written at
 *  ``now`` (INC-14a-1, B-14). A value saved again exactly as it was keeps its
 *  instant: nothing was written. A value of a draft saved before the instants
 *  existed takes the draft's last edit, the latest it can have been written
 *  at, and keeps it from then on instead of following every later edit. */
export function valueTimes(
  draft: VisitDraftRecord,
  fieldId: string,
  value: FieldValue,
  now: number,
): Record<string, string> {
  const known = draft.value_times ?? {};
  const before = new Date(draft.updated_at).toISOString();
  const times: Record<string, string> = {};
  for (const id of Object.keys(draft.values)) times[id] = known[id] ?? before;
  const unchanged = JSON.stringify(draft.values[fieldId]) === JSON.stringify(value);
  times[fieldId] = unchanged
    ? (times[fieldId] ?? new Date(now).toISOString())
    : new Date(now).toISOString();
  return times;
}

/** One value, saved immediately. A reload finds it. */
export async function saveDraftValue(
  database: FieldcoreDb,
  clientGeneratedId: string,
  fieldId: string,
  value: FieldValue,
): Promise<OutboxRecord | null> {
  return mutateDraft(database, clientGeneratedId, (draft, now) => ({
    ...draft,
    values: { ...draft.values, [fieldId]: value },
    value_times: valueTimes(draft, fieldId, value, now),
  }));
}

export interface ObservationTarget {
  scope: ObservationScope;
  field_definition_id?: string | null;
  template_group_id?: string | null;
  /** For corrective actions, which one; others are keyed by scope and target. */
  client_generated_id?: string;
}

function sameTarget(observation: DraftObservation, target: ObservationTarget): boolean {
  if (target.client_generated_id !== undefined) {
    return observation.client_generated_id === target.client_generated_id;
  }
  return (
    observation.scope === target.scope &&
    observation.field_definition_id === (target.field_definition_id ?? null) &&
    observation.template_group_id === (target.template_group_id ?? null)
  );
}

/** The observation of one target, by its text. Empty text removes it. */
export async function saveObservation(
  database: FieldcoreDb,
  clientGeneratedId: string,
  target: ObservationTarget,
  text: string,
  geo: GeoPoint | null = null,
): Promise<OutboxRecord | null> {
  return mutateDraft(database, clientGeneratedId, (draft) => {
    const others = draft.observations.filter((observation) => !sameTarget(observation, target));
    const existing = draft.observations.find((observation) => sameTarget(observation, target));
    if (text.trim() === '') return { ...draft, observations: others };
    const observation: DraftObservation = {
      client_generated_id:
        existing?.client_generated_id ?? target.client_generated_id ?? crypto.randomUUID(),
      scope: target.scope,
      field_definition_id: target.field_definition_id ?? null,
      template_group_id: target.template_group_id ?? null,
      text,
      recorded_at: existing?.recorded_at ?? new Date().toISOString(),
      geo: existing?.geo ?? geo,
    };
    return { ...draft, observations: [...others, observation] };
  });
}

export function findObservation(
  draft: VisitDraftRecord,
  target: ObservationTarget,
): DraftObservation | undefined {
  return draft.observations.find((observation) => sameTarget(observation, target));
}

/** Start the visit: the draft becomes started and revision 1 is queued. Idempotent per draft.
 *
 *  INC-06b-2b (D-182): an unplanned visit carries the code of its reason and,
 *  under `other`, the operator's words; a planned one carries neither and
 *  names its slot instead (D-113). */
export async function startDraft(
  database: FieldcoreDb,
  clientGeneratedId: string,
  reason: { code: UnplannedReasonCode; text: string } | null,
  startedAt: Date,
  geoStart: GeoPoint | null,
): Promise<OutboxRecord | null> {
  return database.transaction('rw', [database.visit_drafts, database.outbox_data], async () => {
    const draft = await database.visit_drafts.get(clientGeneratedId);
    if (draft === undefined) return null;
    if (draft.status !== 'preparing') {
      const pending = await database.outbox_data
        .where('client_generated_id')
        .equals(clientGeneratedId)
        .filter((item) => item.status === 'queued' || item.status === 'sending')
        .first();
      return pending ?? null;
    }
    const now = Date.now();
    const planned = draft.visit_kind === 'planned' && draft.visit_slot_id !== null;
    // The reason the caller gives, or the one the draft already kept: the
    // operator chose it before this screen, and a reload must not lose it.
    const code = planned ? null : (reason?.code ?? draft.unplanned_reason_code);
    const text = reason?.text ?? reasonTextOf(draft.unplanned_reason);
    const started: VisitDraftRecord = {
      ...draft,
      status: 'started',
      started_at: startedAt.toISOString(),
      unplanned_reason_code: code,
      unplanned_reason: code === null ? null : unplannedReason(code, text),
      geo_start: geoStart,
      updated_at: now,
    };
    return enqueueRevision(database, started, now);
  });
}

/** Queue the close: the draft goes to `closing` and the next revision
 *  carries the end instant and the end fix. The server decides. */
/** Queue the close. It runs on the visit being captured, which is the only
 *  state mutateDraft accepts, and moves it to `closing` itself. */
export async function closeDraft(
  database: FieldcoreDb,
  clientGeneratedId: string,
  endedAt: Date,
  geoEnd: GeoPoint | null,
): Promise<OutboxRecord | null> {
  return mutateDraft(database, clientGeneratedId, (draft) => ({
    ...draft,
    status: draft.status === 'started' ? 'closing' : draft.status,
    ended_at: endedAt.toISOString(),
    geo_end: geoEnd,
    missing: null,
  }));
}

/** What the server answered about a revision, applied to the draft. */
export interface ServerAnswer {
  result: 'created' | 'updated' | 'unchanged' | 'rejected';
  code: string | null;
  visit_id: string | null;
  version: number | null;
  client_revision: number | null;
  status: string | null;
  sync_status: string | null;
  missing: MissingItem[] | null;
  /** The companion a companion rejection names, from the answer's details. */
  companion_id?: string | null;
}

/** The rejections that say the slot a planned visit names cannot back it any
 *  more, and what the operator is told (D-160). The server's codes are
 *  app.services.visit_slots'. */
export const CONVERSION_CODES: Record<string, ConversionReason> = {
  planning_disabled: 'planning_disabled',
  slot_not_found: 'slot_not_found',
  // From the operator's side a slot of another site is a slot that is not there.
  slot_site_mismatch: 'slot_not_found',
  slot_cancelled: 'slot_cancelled',
  slot_consumed: 'slot_consumed',
};

/** The rejections that name one companion the organization does not accept
 *  (D-110). The companion is marked on the draft and the operator removes it. */
const COMPANION_REJECTIONS = new Set([
  'companion_not_found',
  'companion_inactive',
  'companion_not_operations',
  'companion_repeated',
  'companion_is_lead',
]);

/** True when the server never took a revision of this visit: a conversion is
 *  only ever done before the first accepted synchronisation (criterion 61). */
export function neverSynchronised(draft: VisitDraftRecord): boolean {
  return draft.server_visit_id === null && draft.synced_revision === 0;
}

export function applyServerAnswer(
  draft: VisitDraftRecord,
  sentRevision: number,
  answer: ServerAnswer,
  now: number,
): VisitDraftRecord {
  const known: Partial<VisitDraftRecord> = {
    server_visit_id: answer.visit_id ?? draft.server_visit_id,
    version: answer.version ?? draft.version,
    server_status: answer.status ?? draft.server_status,
    sync_status:
      answer.sync_status === 'complete' || answer.sync_status === 'data_synced_photos_pending'
        ? answer.sync_status
        : draft.sync_status,
    updated_at: now,
  };
  if (answer.result !== 'rejected') {
    const synced = Math.max(draft.synced_revision, answer.client_revision ?? sentRevision);
    const closed = answer.status === 'closed';
    return {
      ...draft,
      ...known,
      synced_revision: synced,
      missing: null,
      status: closed
        ? 'frozen'
        : draft.status === 'closing' && synced >= draft.client_revision
          ? 'started'
          : draft.status,
      frozen_reason: closed ? 'closed' : draft.frozen_reason,
    };
  }
  if (answer.code === 'close_incomplete') {
    return {
      ...draft,
      ...known,
      synced_revision: Math.max(draft.synced_revision, sentRevision),
      status: draft.status === 'closing' ? 'started' : draft.status,
      missing: answer.missing ?? [],
    };
  }
  if (answer.code === 'visit_closed') {
    return { ...draft, ...known, status: 'frozen', frozen_reason: 'visit_closed' };
  }
  // D-160: the slot cannot back the visit any more. The visit becomes
  // unplanned — additional work outside the plan — and the operator is told.
  // Only before the first accepted revision: once the envelope was born its
  // kind and its slot are the server's and no revision changes them (D-113).
  const conversion = answer.code === null ? undefined : CONVERSION_CODES[answer.code];
  if (conversion !== undefined && neverSynchronised(draft)) {
    return {
      ...draft,
      ...known,
      visit_kind: 'unplanned',
      visit_slot_id: null,
      unplanned_reason_code: 'extra',
      unplanned_reason: 'extra',
      converted_reason: conversion,
      rejected_code: null,
    };
  }
  // D-110: one companion the organization does not accept. It is marked so the
  // operator can take it out, and the rest of the visit is untouched.
  if (answer.code !== null && COMPANION_REJECTIONS.has(answer.code)) {
    const companion = answer.companion_id ?? null;
    if (companion === null) return { ...draft, ...known, rejected_code: answer.code };
    const others = draft.rejected_companions.filter((row) => row.operator_id !== companion);
    return {
      ...draft,
      ...known,
      rejected_companions: [...others, { operator_id: companion, code: answer.code }],
      rejected_code: null,
    };
  }
  // Anything else the server will not take: kept, shown, never discarded
  // (D-176), and tried again at this operator's next sign in.
  return { ...draft, ...known, rejected_code: answer.code };
}
